import React, { useEffect, useState } from "react";
import { McTable } from "@maersk-global/mds-react-wrapper/components-core";
import WarningBanner from "../WarningBanner";
import AgingDonut from "../charts/AgingDonut";
import AgingRankedList from "../charts/AgingRankedList";
import { pick } from "../metricsAdapters";
import "../Analytics.css";

// Aging buckets are a status job (severity of how overdue something is), not
// generic categories — colors are the reserved status palette, never cycled
// categorical hues (dataviz color-formula.md).
const BUCKETS = [
  { key: "within_sla", label: "0–4 Days (Within SLA)", color: "#0ca30c" },
  { key: "days_5_10", label: "5–10 Days", color: "#fab219" },
  { key: "days_11_30", label: "11–30 Days", color: "#ec835a" },
  { key: "above_30_days", label: "Above 30 Days", color: "#d03b3b" },
];

// Explicit columns instead of genericColumns(): each aging_matrix row's
// bucket fields are nested { label, count, amount } objects, which
// genericColumns would render as-is (React prints "[object Object]" for a
// raw object child) — these need to be flattened to a display string first.
const TABLE_COLUMNS = [
  { id: "approver_name", label: "Approver" },
  ...BUCKETS.map((b) => ({ id: b.key, label: b.label })),
  { id: "total_count", label: "Total Count" },
];

const rowLabel = (row) => pick(row, ["approver_name", "approver", "owner", "name"], "Unknown");

const formatBucketCell = (row, bucketKey) => {
  const count = Number(pick(row, [`${bucketKey}.count`], 0)) || 0;
  return count ? String(count) : "—";
};

const useAgingData = (fetcher, filters, refreshKey) => {
  const [state, setState] = useState({ loading: true, data: null, error: null });

  useEffect(() => {
    let cancelled = false;
    setState((prev) => ({ ...prev, loading: true }));

    fetcher(filters)
      .then((data) => {
        if (!cancelled) setState({ loading: false, data, error: null });
      })
      .catch((err) => {
        if (!cancelled) setState({ loading: false, data: null, error: err.message });
      });

    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [fetcher, filters, refreshKey]);

  return state;
};

const deriveColumn = ({ id, title }, { loading, data, error }) => {
  const matrix = pick(data, ["aging_matrix", "matrix", "approver_matrix"], []) || [];

  const rankedRows = matrix.map((row, idx) => {
    const bucketAmounts = BUCKETS.map((b) => Number(pick(row, [`${b.key}.amount`], 0)) || 0);
    let dominantIdx = 0;
    bucketAmounts.forEach((amt, i) => {
      if (amt > bucketAmounts[dominantIdx]) dominantIdx = i;
    });
    return {
      key: `${rowLabel(row)}-${idx}`,
      label: rowLabel(row),
      totalCount: Number(pick(row, ["total_count"], 0)) || 0,
      totalAmount: Number(pick(row, ["total_amount"], 0)) || 0,
      dominantColor: BUCKETS[dominantIdx].color,
      dominantLabel: BUCKETS[dominantIdx].label,
    };
  });

  const tableData = matrix.map((row, idx) => {
    const flat = { _id: `${rowLabel(row)}-${idx}`, approver_name: rowLabel(row) };
    BUCKETS.forEach((b) => {
      flat[b.key] = formatBucketCell(row, b.key);
    });
    flat.total_count = Number(pick(row, ["total_count"], 0)) || 0;
    return flat;
  });

  return {
    id,
    title,
    loading,
    data,
    error,
    matrix,
    rankedRows,
    tableData,
    kpiCards: pick(data, ["kpi_cards"], {}) || {},
  };
};

// Renders two aging metrics (Pending Approval / Pending PO) side by side,
// under one shared header, with each metric's donut + top-owners summary in
// one row and their full breakdown tables in the next — so the two are easy
// to compare instead of scrolling between two stacked full-width sections.
// Each metric's data is fetched exactly once here and reused for both rows.
// `metrics` is always exactly [approval, po], so both hook calls are
// unconditional and fixed in number, as React's rules of hooks require.
const AgingComparisonSection = ({ id, title, metrics, filters, refreshKey }) => {
  const [left, right] = metrics;
  const leftState = useAgingData(left.fetcher, filters, refreshKey);
  const rightState = useAgingData(right.fetcher, filters, refreshKey);
  const columns = [deriveColumn(left, leftState), deriveColumn(right, rightState)];

  return (
    <section id={id} className="analytics-section">
      <div className="analytics-section-header">
        <h2 className="analytics-section-heading">{title}</h2>
      </div>

      <div className="aging-compare-grid">
        {columns.map((m) => (
          <div key={m.id} id={m.id} className="analytics-section-card aging-compare-col">
            <div className="aging-compare-col-header">
              <h3 className="aging-compare-col-title">{m.title}</h3>
              <span className="analytics-section-caption">{m.matrix.length} approver rows</span>
            </div>

            <WarningBanner warning={m.data?.warning} />
            {m.error && <div className="overview-card-error">Failed to load {m.title}: {m.error}</div>}
            {m.loading && !m.data && <div className="overview-card-loading">Loading…</div>}

            {m.data && (
              <>
                <h4 className="analytics-section-title">Aging Summary</h4>
                <AgingDonut
                  buckets={BUCKETS}
                  data={m.kpiCards}
                  totalCount={pick(m.data, ["total_count"], 0)}
                  totalAmount={pick(m.data, ["total_amount"], 0)}
                />

                <h4 className="analytics-section-title aging-compare-subtitle">Top Overdue Owners</h4>
                <AgingRankedList rows={m.rankedRows} maxItems={6} />
              </>
            )}
          </div>
        ))}
      </div>

      <div className="aging-compare-grid">
        {columns.map((m) => (
          <div key={m.id} className="analytics-section-card">
            <details className="analytics-collapsible" open>
              <summary>{m.title} — Full breakdown — {m.tableData.length} rows</summary>
              <McTable data={m.tableData} datakey="_id" zebrastripes truncate fit="small" columns={TABLE_COLUMNS} />
            </details>
          </div>
        ))}
      </div>
    </section>
  );
};

export default AgingComparisonSection;
